import { queryOptions } from "@tanstack/react-query";
import { fieldInfoService } from "./field-info.service";
import type {
	FieldInfoLocalizationsParams,
	FieldInfoParams,
} from "./field-info.types";

// ==================== QUERY KEYS ====================

export const fieldInfoKeys = {
	all: ["field-info"] as const,

	byEntityType: (entityType: string) =>
		[...fieldInfoKeys.all, "entityType", entityType] as const,

	list: (entityType: string, params?: FieldInfoParams) =>
		[...fieldInfoKeys.byEntityType(entityType), "list", params] as const,

	label: (entityType: string, params?: FieldInfoParams) =>
		[...fieldInfoKeys.byEntityType(entityType), "label", params] as const,

	orderBys: (entityType: string) =>
		[...fieldInfoKeys.byEntityType(entityType), "order-bys"] as const,

	localizations: (params?: FieldInfoLocalizationsParams) =>
		[...fieldInfoKeys.all, "localizations", params] as const,
} as const;

// ==================== QUERY OPTIONS ====================

export const fieldInfoQueryOptions = {
	/**
	 * Get field infos for an entity type
	 * GET /field-infos/{entityType}
	 */
	list: (entityType: string, params?: FieldInfoParams) =>
		queryOptions({
			queryKey: fieldInfoKeys.list(entityType, params),
			queryFn: () => fieldInfoService.getFieldInfos(entityType, params),
			staleTime: 24 * 60 * 60 * 1000, // 24 hours
			gcTime: 24 * 60 * 60 * 1000, // 24 hours
			enabled: !!entityType && entityType.length > 0,
		}),

	/**
	 * Get field infos (label) for an entity type
	 * GET /field-infos/{entityType}/label
	 */
	label: (entityType: string, params?: FieldInfoParams) =>
		queryOptions({
			queryKey: fieldInfoKeys.label(entityType, params),
			queryFn: () => fieldInfoService.getFieldInfosLabel(entityType, params),
			staleTime: 10 * 60 * 1000, // 10 minutes
			gcTime: 30 * 60 * 1000, // 30 minutes
			enabled: !!entityType && entityType.length > 0,
		}),

	/**
	 * Get field info localizations
	 * GET /field-infos/localizations
	 */
	localizations: (params?: FieldInfoLocalizationsParams) =>
		queryOptions({
			queryKey: fieldInfoKeys.localizations(params),
			queryFn: () => fieldInfoService.getFieldInfosLocalizations(params),
			staleTime: 24 * 60 * 60 * 1000, // 24 hours
			gcTime: 24 * 60 * 60 * 1000, // 24 hours
			// i18n applies from this cache; avoid background refetch overwriting strings after saves.
			refetchOnWindowFocus: false,
		}),

	/**
	 * Get order-by options for an entity type
	 * GET /field-infos/{entityType}/order-bys
	 */
	orderBys: (entityType: string) =>
		queryOptions({
			queryKey: fieldInfoKeys.orderBys(entityType),
			queryFn: () => fieldInfoService.getFieldInfosOrderBys(entityType),
			staleTime: 10 * 60 * 1000, // 10 minutes
			gcTime: 30 * 60 * 1000, // 30 minutes
			enabled: !!entityType && entityType.length > 0,
		}),
};
